import { isValidRegionSlug } from 'lib/region/regionLocale';

/**
 * Consent policy for the next analytics stack.
 *
 * Port of the locked zone table: each category turns on at the first moment
 * its mode allows, and a storefront can only tighten the visitor's IP zone.
 * The GTM container still contains ad tags, so GTM waits for ads until
 * `GTM_CONTAINER_CLEAN` is set.
 */

export const GTM_CONTAINER_CLEAN = false;

const INDIA_OPT_IN_MS = Date.parse('2027-05-13T00:00:00Z');
const SRI_LANKA_OPT_IN_MS = Date.parse('2027-01-01T00:00:00Z');

export type Mode = 'NOTICE' | 'OUT' | 'IN';
export type ZoneId =
  | 'strict'
  | 'uk'
  | 'ch'
  | 'us'
  | 'ca'
  | 'in'
  | 'optin'
  | 'analyticsOut'
  | 'optout';

export type ConsentChoice = 'none' | 'accept' | 'reject' | 'analytics' | 'custom';

/** Switches the visitor set in Privacy Choices. Replay follows analytics. */
export type ConsentCategorySelection = {
  stats: boolean;
  analytics: boolean;
  ads: boolean;
};
export type PageTier = 'marketing' | 'health' | 'private';
export type Category = 'stats' | 'analytics' | 'replay' | 'ads' | 'attribution';

export type ZoneModes = { stats: Mode; analytics: Mode; ads: Mode };

export const ZONES: Record<ZoneId, ZoneModes> = {
  strict: { stats: 'IN', analytics: 'IN', ads: 'IN' },
  uk: { stats: 'OUT', analytics: 'IN', ads: 'IN' },
  ch: { stats: 'OUT', analytics: 'OUT', ads: 'IN' },
  us: { stats: 'NOTICE', analytics: 'IN', ads: 'IN' },
  ca: { stats: 'NOTICE', analytics: 'OUT', ads: 'IN' },
  in: { stats: 'NOTICE', analytics: 'OUT', ads: 'OUT' },
  optin: { stats: 'NOTICE', analytics: 'IN', ads: 'IN' },
  analyticsOut: { stats: 'NOTICE', analytics: 'OUT', ads: 'IN' },
  optout: { stats: 'NOTICE', analytics: 'OUT', ads: 'OUT' },
};

const RANK: Record<Mode, number> = { NOTICE: 0, OUT: 1, IN: 2 };

/** Countries with an explicit zone. Anything else, including unknown, is strict. */
export const COUNTRY_ZONES: Record<string, ZoneId> = {
  US: 'us',
  GB: 'uk',
  CA: 'ca',
  CH: 'ch',
  IN: 'in',
  NG: 'strict',
  AU: 'optout',
  JP: 'optout',
  SG: 'optout',
  MX: 'optout',
  NZ: 'optout',
  HK: 'optout',
  TW: 'optout',
  MY: 'optout',
  PK: 'optout',
  LK: 'optout',
  AE: 'optin',
  SA: 'optin',
  TR: 'optin',
  IL: 'optin',
  BR: 'optin',
  AR: 'optin',
  CL: 'optin',
  CO: 'optin',
  PE: 'optin',
  KE: 'optin',
  VN: 'optin',
  TH: 'optin',
  PH: 'optin',
  ID: 'optin',
  CN: 'optin',
  BD: 'optin',
  EG: 'optin',
  OM: 'optin',
  JO: 'optin',
  KW: 'optin',
  ZA: 'analyticsOut',
  KR: 'analyticsOut',
  QA: 'analyticsOut',
  BH: 'analyticsOut',
  AT: 'strict',
  BE: 'strict',
  BG: 'strict',
  HR: 'strict',
  CY: 'strict',
  CZ: 'strict',
  DK: 'strict',
  EE: 'strict',
  FI: 'strict',
  FR: 'strict',
  DE: 'strict',
  GR: 'strict',
  HU: 'strict',
  IE: 'strict',
  IT: 'strict',
  LV: 'strict',
  LT: 'strict',
  LU: 'strict',
  MT: 'strict',
  NL: 'strict',
  PL: 'strict',
  PT: 'strict',
  RO: 'strict',
  SK: 'strict',
  SI: 'strict',
  ES: 'strict',
  SE: 'strict',
  NO: 'strict',
  IS: 'strict',
  LI: 'strict',
};

export const POLICY_COUNTRIES: readonly string[] = Object.keys(COUNTRY_ZONES);

const PRIVATE_PREFIXES = [
  '/cycle-report',
  '/forms/metabolic-health-assessment',
  '/blood-vision/serviceability-check',
  '/order',
  '/emerald-sphere',
];

const HEALTH_PREFIXES = [
  '/blood-vision',
  '/performance-lab/glp',
  '/performance-lab/iv-lounge',
  '/performance-lab/recovery',
  '/womens-health',
  '/ultrahuman-x-clue',
  '/pulsomics',
  '/m1',
  '/ring/buy/better-help',
];

export type EffectiveConsent = Record<Category, boolean>;

export type ConsentDecision = {
  ipZone: ZoneId;
  storefrontZone: ZoneId | null;
  modes: ZoneModes;
  effective: EffectiveConsent;
  page: PageTier;
};

export type DecideInput = {
  country: string | null | undefined;
  province?: string | null;
  /** Storefront country (US, CA, GB, …). Null skips the tighten step. */
  storefrontCountry?: string | null;
  choice?: ConsentChoice;
  /**
   * Explicit switches. They replace Accept / Reject presets. Global Privacy
   * Control still forces ads off, and a private page still loads nothing.
   * Notice-only statistics stay on only when this map is absent.
   */
  categories?: ConsentCategorySelection | null;
  gpc?: boolean;
  page?: PageTier;
  now?: Date;
  /** Kept for tests. Live traffic always leaves this on. */
  tripleWhale?: boolean;
};

function stricter(a: Mode, b: Mode): Mode {
  return RANK[a] >= RANK[b] ? a : b;
}

/**
 * A Canada IP with no province is treated as Quebec. `XX` is the server's
 * missing-header placeholder.
 */
export function isQuebec(
  country: string | null | undefined,
  province: string | null | undefined,
): boolean {
  if ((country ?? '').toUpperCase() !== 'CA') return false;
  if (province == null) return true;
  const value = province.trim().toUpperCase();
  if (value === '' || value === 'XX') return true;
  return value === 'QC';
}

export function zoneFor(
  country: string | null | undefined,
  now: Date,
  quebec: boolean,
): ZoneId {
  const code = (country ?? '').toUpperCase();
  if (!code) return 'strict';
  if (code === 'CA' && quebec) return 'strict';
  if (code === 'IN' && now.getTime() >= INDIA_OPT_IN_MS) return 'optin';
  if (code === 'LK' && now.getTime() >= SRI_LANKA_OPT_IN_MS) return 'optin';
  return COUNTRY_ZONES[code] ?? 'strict';
}

function normalizePath(pathname: string): string {
  const bare = pathname.split('?')[0]?.split('#')[0] ?? '/';
  const withSlash = bare.startsWith('/') ? bare : `/${bare}`;
  if (withSlash.length > 1 && withSlash.endsWith('/')) {
    return withSlash.slice(0, -1);
  }
  return withSlash || '/';
}

function stripRegion(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (first && isValidRegionSlug(first)) {
    const rest = segments.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return pathname;
}

function matchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function classifyRoute(pathname: string): PageTier {
  const path = normalizePath(stripRegion(normalizePath(pathname)));
  if (PRIVATE_PREFIXES.some((prefix) => matchesPrefix(path, prefix))) {
    return 'private';
  }
  if (HEALTH_PREFIXES.some((prefix) => matchesPrefix(path, prefix))) {
    return 'health';
  }
  return 'marketing';
}

export function isGtmAllowed(
  effective: Pick<EffectiveConsent, 'analytics' | 'ads'>,
  containerClean: boolean = GTM_CONTAINER_CLEAN,
): boolean {
  return effective.analytics && (effective.ads || containerClean);
}

export function decide(input: DecideInput): ConsentDecision {
  const now = input.now ?? new Date();
  const country = input.country ?? '';
  const choice = input.choice ?? 'none';
  const gpc = input.gpc ?? false;
  const page = input.page ?? 'marketing';
  const tripleWhale = input.tripleWhale ?? true;
  const quebec = isQuebec(country, input.province);

  const ipZone = zoneFor(country, now, quebec);
  const storeCode = input.storefrontCountry?.toUpperCase() || null;
  const storefrontZone = storeCode ? zoneFor(storeCode, now, false) : null;

  const ipModes = ZONES[ipZone];
  const modes: ZoneModes = storefrontZone
    ? {
        stats: stricter(ipModes.stats, ZONES[storefrontZone].stats),
        analytics: stricter(ipModes.analytics, ZONES[storefrontZone].analytics),
        ads: stricter(ipModes.ads, ZONES[storefrontZone].ads),
      }
    : ipModes;

  const explicit =
    choice === 'accept'
      ? { stats: true, analytics: true, ads: true, attribution: true }
      : choice === 'reject'
        ? { stats: false, analytics: false, ads: false, attribution: false }
        : choice === 'analytics'
          ? { stats: true, analytics: true, ads: false, attribution: false }
          : null;

  const effective: EffectiveConsent = {
    stats: false,
    analytics: false,
    replay: false,
    ads: false,
    attribution: false,
  };

  if (page === 'private') {
    return { ipZone, storefrontZone, modes, effective, page };
  }

  if (input.categories) {
    effective.stats = input.categories.stats;
    effective.analytics = input.categories.analytics;
    effective.ads = gpc ? false : input.categories.ads;
    effective.replay = input.categories.analytics;
    return { ipZone, storefrontZone, modes, effective, page };
  }

  if (modes.stats === 'NOTICE') {
    effective.stats = true;
  } else if (explicit) {
    effective.stats = explicit.stats;
  } else if (modes.stats === 'OUT') {
    effective.stats = true;
  }

  if (explicit) {
    effective.analytics = explicit.analytics;
  } else if (modes.analytics === 'OUT' && !gpc) {
    effective.analytics = true;
  }

  if (gpc) {
    effective.ads = false;
  } else if (explicit) {
    effective.ads = explicit.ads;
  } else if (modes.ads === 'OUT') {
    effective.ads = true;
  }

  if (explicit?.analytics) {
    effective.replay = true;
  }

  if (!gpc && page !== 'health' && tripleWhale && explicit?.attribution) {
    effective.attribution = true;
  }

  return { ipZone, storefrontZone, modes, effective, page };
}

/**
 * Opt-out categories stay off until the banner is on screen, unless the
 * visitor already made a choice. Notice-only and explicit grants do not wait.
 */
export function releaseOptOut(
  mode: Mode,
  allowed: boolean,
  choice: ConsentChoice,
  bannerReady: boolean,
): boolean {
  if (!allowed) return false;
  if (mode === 'OUT' && choice === 'none' && !bannerReady) return false;
  return true;
}
